import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  CareRecordSchema,
  CareRecordCreateInputSchema,
  CareRecordUpdateInputSchema,
  type CareRecord,
  type CareRecordCreateInput,
  type CareRecordUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `careRecord` (entrée de suivi individualisé, org-wide en v0).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`CareRecordCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Sécurité : org-wide en v0 (même doctrine qu'asset). Le contenu sensible (texte
 * libre de santé, `details`) ne transite PAS ici : il ira dans un sous-document
 * privé après v0 (cf. @taki/data-model). L'accès fin (owner + rôle
 * soignant/encadrant désigné pour les sujets humains) viendra ensuite.
 */

export type { CareRecordCreateInput };

/** Nom de la collection Firestore de la famille careRecord. */
export const CARE_RECORDS_COLLECTION = 'careRecords';

/**
 * Repository typé pour la famille `careRecord`.
 * Toute lecture est validée par `CareRecordSchema`, toute écriture par
 * `CareRecordCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface CareRecordRepository {
  create(input: CareRecordCreateInput): Promise<CareRecord>;
  list(options?: ListOptions): Promise<CareRecord[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<CareRecord | null>;
  update(id: string, patch: CareRecordUpdateInput): Promise<CareRecord>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<CareRecord>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<CareRecord>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<CareRecord>;
}

const careRecordConverter = createConverter<CareRecord>((raw: DocumentData) => CareRecordSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createCareRecordRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): CareRecordRepository {
  const col = collection(db, CARE_RECORDS_COLLECTION).withConverter(careRecordConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<CareRecord>(db, col, CARE_RECORDS_COLLECTION, getActorId);

  return {
    async create(input: CareRecordCreateInput): Promise<CareRecord> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = CareRecordCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.assetId !== undefined) optional['assetId'] = fields.assetId;
      if (fields.contactId !== undefined) optional['contactId'] = fields.contactId;
      if (fields.performedAt !== undefined) optional['performedAt'] = fields.performedAt;
      if (fields.practitionerWorkerId !== undefined) optional['practitionerWorkerId'] = fields.practitionerWorkerId;
      if (fields.nextDueAt !== undefined) optional['nextDueAt'] = fields.nextDueAt;
      if (fields.documentId !== undefined) optional['documentId'] = fields.documentId;
      if (fields.measurementId !== undefined) optional['measurementId'] = fields.measurementId;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.workOrderId !== undefined) optional['workOrderId'] = fields.workOrderId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as CareRecord);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return CareRecordSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<CareRecord[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<CareRecord | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: CareRecordUpdateInput): Promise<CareRecord> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = CareRecordUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, CARE_RECORDS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`careRecord ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
